/* eslint-disable indent */
// index.tsx

import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ArrowUpDown, Filter, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { FilterFieldDTO } from "@/types";
import { colourScheme } from "@/utils/styling/colours";
import * as Styled from "./SearchAccordion.styled";
type SearchAccordionProps = {
  filterFields: FilterFieldDTO[];
  filters: Record<string, any>;
  handleFilterChange: (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => void;
  query: string;
  setQuery: Dispatch<SetStateAction<string>>;
  sort: string;
  setSort: React.Dispatch<React.SetStateAction<string>>;
  sortOrder: string;
  setSortOrder: React.Dispatch<React.SetStateAction<string>>;
  pageSize: number;
  handleChangePageSize: (value: number) => void;
  currentPage: number;
  totalPages: number;
  handlePrevPage: () => void;
  handleNextPage: () => void;
  disabledFilters?: string[];
};
const sortOptions = [{
  value: "highestrated",
  label: "Highest Rated (All Time)"
}, {
  value: "highestratedyear",
  label: "Highest Rated (Year)"
}, {
  value: "highestratedmonth",
  label: "Highest Rated (Month)"
}, {
  value: "highestratedweek",
  label: "Highest Rated (Week)"
}, {
  value: "highestratedday",
  label: "Highest Rated (Day)"
}, {
  value: "highestratedhour",
  label: "Highest Rated (Hour)"
}, {
  value: "createdAt",
  label: "Most Recent"
}];
const SearchAccordion: React.FC<SearchAccordionProps & {
  children: React.ReactNode;
}> = ({
  filterFields,
  filters,
  handleFilterChange,
  query,
  setQuery,
  sort,
  setSort,
  sortOrder,
  setSortOrder,
  pageSize,
  handleChangePageSize,
  currentPage,
  totalPages,
  handlePrevPage,
  handleNextPage,
  disabledFilters = [],
  children
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === "asc" ? "desc" : "asc");
  };
  useEffect(() => {
    setSort("createdAt");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <Styled.Container data-sentry-element="unknown" data-sentry-component="SearchAccordion" data-sentry-source-file="index.tsx">
            {/* Header */}
            <Styled.Header data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.HeaderContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.HeaderFlex data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Styled.SearchAndFilter data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen} data-sentry-element="Sheet" data-sentry-source-file="index.tsx">
                                <SheetTrigger asChild data-sentry-element="SheetTrigger" data-sentry-source-file="index.tsx">
                                    <Button variant="outline" className="rounded-full" style={{
                  backgroundColor: colourScheme.primary,
                  color: colourScheme.secondary,
                  borderColor: colourScheme.secondaryLight
                }} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                        <Filter className="mr-2 h-4 w-4" data-sentry-element="Filter" data-sentry-source-file="index.tsx" />{" "}
                                        Filters
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="left" className="w-full sm:w-[540px] flex flex-col" style={{
                backgroundColor: colourScheme.primary,
                color: colourScheme.white,
                borderColor: colourScheme.primaryLight
              }} data-sentry-element="SheetContent" data-sentry-source-file="index.tsx">
                                    <SheetHeader className="mt-[5rem] pr-4" data-sentry-element="SheetHeader" data-sentry-source-file="index.tsx">
                                        <SheetTitle style={{
                    color: colourScheme.secondary
                  }} data-sentry-element="SheetTitle" data-sentry-source-file="index.tsx">
                                            Search and Filters
                                        </SheetTitle>
                                        <SheetDescription style={{
                    color: colourScheme.greyLight
                  }} data-sentry-element="SheetDescription" data-sentry-source-file="index.tsx">
                                            Refine your search with advanced
                                            options and filters.
                                        </SheetDescription>
                                    </SheetHeader>

                                    <ScrollArea className="flex-1 overflow-y-auto mt-6 pr-4" data-sentry-element="ScrollArea" data-sentry-source-file="index.tsx">
                                        <div className="space-y-6">
                                            {/* Filters Section */}
                                            <div className="space-y-4">
                                                <h3 className="text-lg font-semibold" style={{
                        color: colourScheme.secondary
                      }}>
                                                    Filters
                                                </h3>
                                                {filterFields.map(field => <div key={field.label} className="space-y-2">
                                                        <Label className="text-sm font-medium capitalize" style={{
                          color: colourScheme.greyLight
                        }}>
                                                            {field.label}
                                                        </Label>
                                                        {field.isCheckbox ? <div className="flex items-center space-x-2">
                                                                <Checkbox id={field.label} checked={filters[field.label] === true} onCheckedChange={(checked: any) => handleFilterChange({
                            target: {
                              name: field.label,
                              value: checked
                            }
                          } as React.ChangeEvent<HTMLInputElement>)} disabled={disabledFilters.includes(field.label)} style={{
                            borderColor: colourScheme.greyLight
                          }} />
                                                                <Label htmlFor={field.label} style={{
                            color: colourScheme.white
                          }}>
                                                                    {field.label}
                                                                </Label>
                                                            </div> : !field.isFreeText ? <Select value={filters[field.label] || ""} onValueChange={value => handleFilterChange({
                          target: {
                            name: field.label,
                            value
                          }
                        } as React.ChangeEvent<HTMLSelectElement>)} disabled={disabledFilters.includes(field.label)}>
                                                                <SelectTrigger className="w-full" style={{
                            backgroundColor: colourScheme.primaryLight,
                            color: colourScheme.white,
                            borderColor: colourScheme.greyDark
                          }}>
                                                                    <SelectValue placeholder={`Select ${field.label}`} />
                                                                </SelectTrigger>
                                                                <SelectContent style={{
                            backgroundColor: colourScheme.primary,
                            borderColor: colourScheme.primaryLight
                          }}>
                                                                    {field.values?.map(option => <SelectItem key={option} value={option} style={{
                              color: colourScheme.white
                            }}>
                                                                                {option}
                                                                            </SelectItem>)}
                                                                </SelectContent>
                                                            </Select> : <Input type="text" id={field.label} name={field.label} value={filters[field.label] || ""} onChange={handleFilterChange} className="w-full" disabled={disabledFilters.includes(field.label)} style={{
                          backgroundColor: colourScheme.primaryLight,
                          color: colourScheme.white,
                          borderColor: colourScheme.greyDark
                        }} placeholder={`Enter ${field.label}...`} />}
                                                    </div>)}
                                            </div>
                                        </div>
                                    </ScrollArea>
                                    <Styled.SheetFooter className="mt-auto" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                        <Button variant="outline" onClick={() => {
                    filterFields.forEach(field => {
                      handleFilterChange({
                        target: {
                          name: field.label,
                          value: ""
                        }
                      } as React.ChangeEvent<HTMLInputElement>);
                    });
                  }} style={{
                    backgroundColor: colourScheme.primary,
                    color: colourScheme.secondary,
                    borderColor: colourScheme.secondaryLight
                  }} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                            Clear All
                                        </Button>
                                        <Button onClick={() => setIsFilterOpen(false)} style={{
                    backgroundColor: colourScheme.secondary,
                    color: colourScheme.white
                  }} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                            Apply
                                        </Button>
                                    </Styled.SheetFooter>
                                </SheetContent>
                            </Sheet>
                            <Styled.SearchWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{
                color: colourScheme.greyLight
              }} data-sentry-element="Search" data-sentry-source-file="index.tsx" />
                                <Input type="text" placeholder="Search artworks..." className="pl-10 pr-4 py-2 w-full rounded-full" style={{
                backgroundColor: colourScheme.primary,
                color: colourScheme.white,
                borderColor: colourScheme.primaryLight
              }} value={query} onChange={e => setQuery(e.target.value)} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
                            </Styled.SearchWrapper>
                        </Styled.SearchAndFilter>
                    </Styled.HeaderFlex>
                </Styled.HeaderContainer>
            </Styled.Header>

            {/* Main Content */}
            <Styled.MainContent data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                {/* Sort Options and Active Filters */}
                <Styled.SortSection data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.SortOptions data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Styled.SortBy data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Label htmlFor="sort-by" className="text-sm font-medium" style={{
              color: colourScheme.greyLight
            }} data-sentry-element="Label" data-sentry-source-file="index.tsx">
                                Sort by:
                            </Label>
                            <Select value={sort} onValueChange={setSort} data-sentry-element="Select" data-sentry-source-file="index.tsx">
                                <SelectTrigger id="sort-by" className="w-[200px]" style={{
                backgroundColor: colourScheme.primary,
                color: colourScheme.white,
                borderColor: colourScheme.primaryLight
              }} data-sentry-element="SelectTrigger" data-sentry-source-file="index.tsx">
                                    <SelectValue placeholder="Sort by" data-sentry-element="SelectValue" data-sentry-source-file="index.tsx" />
                                </SelectTrigger>
                                <SelectContent style={{
                backgroundColor: colourScheme.primary,
                borderColor: colourScheme.primaryLight
              }} data-sentry-element="SelectContent" data-sentry-source-file="index.tsx">
                                    {sortOptions.map(option => <SelectItem key={option.value} value={option.value} style={{
                  color: colourScheme.white
                }}>
                                            {option.label}
                                        </SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Styled.SortBy>
                        {/* Page size selector */}
                        <Styled.SortBy data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Label htmlFor="sort-by" className="text-sm font-medium" style={{
              color: colourScheme.greyLight
            }} data-sentry-element="Label" data-sentry-source-file="index.tsx">
                                Images per page:{" "}
                            </Label>
                            <Select value={pageSize.toString()} onValueChange={value => handleChangePageSize(Number(value))} data-sentry-element="Select" data-sentry-source-file="index.tsx">
                                <SelectTrigger className="select-trigger" data-sentry-element="SelectTrigger" data-sentry-source-file="index.tsx">
                                    <SelectValue placeholder="Images per Page:" data-sentry-element="SelectValue" data-sentry-source-file="index.tsx" />
                                </SelectTrigger>
                                <SelectContent className="select-content" data-sentry-element="SelectContent" data-sentry-source-file="index.tsx">
                                    <SelectItem key={5} value={"5"} data-sentry-element="SelectItem" data-sentry-source-file="index.tsx">
                                        5
                                    </SelectItem>
                                    <SelectItem key={10} value={"10"} data-sentry-element="SelectItem" data-sentry-source-file="index.tsx">
                                        10
                                    </SelectItem>
                                    <SelectItem key={15} value={"15"} data-sentry-element="SelectItem" data-sentry-source-file="index.tsx">
                                        15
                                    </SelectItem>
                                    <SelectItem key={30} value={"30"} data-sentry-element="SelectItem" data-sentry-source-file="index.tsx">
                                        30
                                    </SelectItem>
                                    <SelectItem key={50} value={"50"} data-sentry-element="SelectItem" data-sentry-source-file="index.tsx">
                                        50
                                    </SelectItem>
                                    <SelectItem key={100} value={"100"} data-sentry-element="SelectItem" data-sentry-source-file="index.tsx">
                                        100
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </Styled.SortBy>
                        <Styled.SortBy data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            {/* Pagination Controls */}
                            <div className="flex items-center justify-center mt-4 gap-2">
                                <Button disabled={currentPage === 0} onClick={handlePrevPage} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                    Prev
                                </Button>
                                Page {currentPage + 1} of {totalPages || 1}
                                <Button disabled={currentPage >= totalPages - 1 || totalPages === 0} onClick={handleNextPage} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                    Next
                                </Button>
                            </div>
                        </Styled.SortBy>
                        <Button variant="outline" onClick={toggleSortOrder} style={{
            backgroundColor: colourScheme.primary,
            color: colourScheme.secondary,
            borderColor: colourScheme.secondaryLight
          }} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            <ArrowUpDown className="mr-2 h-4 w-4" data-sentry-element="ArrowUpDown" data-sentry-source-file="index.tsx" />
                            {sortOrder === "asc" ? "Ascending" : "Descending"}
                        </Button>
                    </Styled.SortOptions>
                    <ScrollArea className="whitespace-nowrap pb-4" data-sentry-element="ScrollArea" data-sentry-source-file="index.tsx">
                        <Styled.ActiveFilters data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            {Object.entries(filters).map(([key, value]) => value && value !== "" ? <Badge key={key} variant="secondary" style={{
              backgroundColor: colourScheme.primaryLight,
              color: colourScheme.secondary
            }}>
                                        {key}: {value.toString()}
                                        <Button variant="ghost" size="sm" className="ml-2 h-4 w-4 p-0" style={{
                color: colourScheme.greyLight
              }} onClick={() => handleFilterChange({
                target: {
                  name: key,
                  value: ""
                }
              } as React.ChangeEvent<HTMLInputElement>)}>
                                            <X className="h-3 w-3" />
                                        </Button>
                                    </Badge> : null)}
                        </Styled.ActiveFilters>
                    </ScrollArea>
                </Styled.SortSection>

                {/* Children */}
                {children}
                <div className="flex items-center justify-center mt-4 gap-2">
                    <Styled.SortBy data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {/* Pagination Controls */}
                        <div className="flex items-center justify-center mt-4 gap-2">
                            <Button disabled={currentPage === 0} onClick={handlePrevPage} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                Prev
                            </Button>
                            Page {currentPage + 1} of {totalPages || 1}
                            <Button disabled={currentPage >= totalPages - 1 || totalPages === 0} onClick={handleNextPage} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                Next
                            </Button>
                        </div>
                    </Styled.SortBy>
                </div>
            </Styled.MainContent>
        </Styled.Container>;
};
export default SearchAccordion;